import React, { useCallback, useState } from 'react';

const PAID_STATUSES = new Set(['paid', 'processing', 'shipped', 'completed', 'partially_shipped']);

function PaymentIcon({ tone }) {
  const cls = `customer-order-payment__icon customer-order-payment__icon--${tone}`;
  if (tone === 'success') {
    return (
      <span className={cls} aria-hidden>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="M8 12.5l2.5 2.5L16 9"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    );
  }
  if (tone === 'pending') {
    return (
      <span className={cls} aria-hidden>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
          <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
        </svg>
      </span>
    );
  }
  return (
    <span className={cls} aria-hidden>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="6" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <path d="M3 10h18M7 14h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    </span>
  );
}

/**
 * @param {object|null} order
 */
export function orderPaymentCustomerSummary(order) {
  if (!order || typeof order !== 'object') return null;

  const status = String(order.status || '').toLowerCase();
  const paidAt = order.paid_at ? String(order.paid_at) : null;
  const rzPaymentId = String(order.razorpay_payment_id || '').trim();
  const rzOrderId = String(order.razorpay_order_id || '').trim();
  const paymentMethod = String(order.payment_method || '').toLowerCase();
  const paymentType = String(order.payment_type || '');
  const paymentInstrument = String(order.payment_instrument || '').trim();
  const razorpayMethod = String(order.razorpay_method || '').trim().toLowerCase();

  const isPaid =
    Boolean(paidAt) || PAID_STATUSES.has(status) || paymentType === 'paid';

  if (isPaid) {
    const paidOnline =
      rzPaymentId !== '' ||
      paymentMethod === 'online' ||
      (paymentMethod !== 'offline' && rzOrderId !== '' && paymentType !== 'offline');

    const paidWith =
      paymentInstrument ||
      (razorpayMethod === 'upi'
        ? 'UPI'
        : razorpayMethod === 'card'
          ? 'Card'
          : razorpayMethod === 'netbanking'
            ? 'Net banking'
            : razorpayMethod === 'wallet'
              ? 'Wallet'
              : razorpayMethod
                ? razorpayMethod.replace(/_/g, ' ')
                : '');

    return {
      tone: 'success',
      badge: 'Paid',
      headline: paidOnline ? 'Paid online' : 'Payment received',
      subline: paidOnline
        ? paidWith || 'Secured by Razorpay'
        : null,
      paidWith: paidWith || null,
      paidAt,
      reference: rzPaymentId || null,
      referenceLabel: 'Payment ID',
      note: paidOnline && !paidWith
        ? 'Payment type will appear here after we sync with Razorpay — refresh in a moment or use the payment ID on your receipt.'
        : null,
    };
  }

  if (paymentType === 'razorpay_pending' || (status === 'pending' && rzOrderId)) {
    return {
      tone: 'pending',
      badge: 'Pending',
      headline: 'Payment not completed',
      subline: 'Your order is saved — finish checkout to pay',
      paidAt: null,
      reference: rzOrderId || null,
      referenceLabel: 'Checkout reference',
      note: 'Use Pay now on checkout or contact us with your order number.',
    };
  }

  return {
    tone: 'offline',
    badge: 'Pay later',
    headline: 'Offline payment',
    subline: 'We will confirm your order manually',
    paidAt: null,
    reference: null,
    referenceLabel: '',
    note: 'UPI or bank details will be sent by email or WhatsApp.',
  };
}

function formatPaidAt(iso) {
  return new Date(iso).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function CopyReferenceButton({ text }) {
  const [copied, setCopied] = useState(false);

  const onCopy = useCallback(async () => {
    const value = String(text || '').trim();
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* fallback ignored */
    }
  }, [text]);

  return (
    <button
      type="button"
      className="customer-order-payment__copy btn btn-sm"
      onClick={onCopy}
      aria-label="Copy payment reference"
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

export function CustomerOrderPaymentSection({ order, className = '' }) {
  const info = orderPaymentCustomerSummary(order);
  if (!info) return null;

  const paidLabel = info.paidAt ? formatPaidAt(info.paidAt) : null;
  const rootClass = [
    'customer-order-payment',
    `customer-order-payment--${info.tone}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={rootClass} aria-labelledby="customer-order-payment-title">
      <div className="customer-order-payment__head">
        <PaymentIcon tone={info.tone} />
        <div className="customer-order-payment__intro">
          <p id="customer-order-payment-title" className="customer-order-payment__eyebrow">
            Payment
          </p>
          <div className="customer-order-payment__title-row">
            <h3 className="customer-order-payment__headline">{info.headline}</h3>
            <span className={`customer-order-payment__badge customer-order-payment__badge--${info.tone}`}>
              {info.badge}
            </span>
          </div>
          {info.subline ? <p className="customer-order-payment__subline">{info.subline}</p> : null}
        </div>
      </div>

      {(paidLabel || info.paidWith || info.reference) && (
        <dl className="customer-order-payment__meta">
          {info.paidWith ? (
            <>
              <dt>Paid with</dt>
              <dd>{info.paidWith}</dd>
            </>
          ) : null}
          {paidLabel ? (
            <>
              <dt>Paid on</dt>
              <dd>{paidLabel}</dd>
            </>
          ) : null}
          {info.reference ? (
            <>
              <dt>{info.referenceLabel}</dt>
              <dd className="customer-order-payment__ref-cell">
                <code className="customer-order-payment__ref">{info.reference}</code>
                <CopyReferenceButton text={info.reference} />
              </dd>
            </>
          ) : null}
        </dl>
      )}

      {info.note ? <p className="customer-order-payment__note">{info.note}</p> : null}
    </section>
  );
}
